// Step 2: Firma Belgeleri
import { DependencyRuleNote } from '../../documents/docDependencies';
import { useEffect, useState } from 'react';
import { confirmDialog } from '../../ConfirmHost';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { FileText, Users, ChevronDown, ChevronUp, Eye, CheckCircle, Trash2, AlertTriangle } from 'lucide-react';
import { DocumentUploadButton } from '../../employees/DocumentUploadButton';
import { DocumentViewerDialog } from '../../employees/DocumentViewerDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { FirmDeficiencyActions, FirmDeficiencyExtra } from '../../documents/FirmDocCell';
import { DocDecisionPanel } from '../../documents/DocDecisionPanel';
import { SgkEmployeeChecklist } from './sgkCheck';
import { useAuth } from '../../../context/AuthContext';
import { COMPANY_DOCS, DOC_STATUS_OPTIONS as COMPANY_DOC_STATUS } from '../../companies/constants';
import { PageCountBadge } from '../../documents/PageCountBadge';
import { PageLimitTag } from '../../documents/PageLimitTag';

const PERSON_DOCS = {
  isg_katip_uzman: { label: 'İSG Katip Uzman Adı Soyadı', field: 'isg_katip_uzman_name', color: 'text-blue-600' },
  isg_katip_hekim: { label: 'İşyeri Hekimi Adı Soyadı', field: 'isg_katip_hekim_name', color: 'text-emerald-600' },
};

export const sgkNeedsReviewFor = (company, permitEmployees, active) => {
  const docs = company?.documents || {};
  return !!active && docs.sgk_hizmet_listesi === 'onay' && permitEmployees.some(e => !(docs.sgk_hizmet_listesi_reviewed_ids || []).includes(e.id));
};
// İnceleyen için: SGK listesinde kontrol edilmemiş personel varsa onaylı liste "Onay Bekliyor" sayılır
export const withSgkReview = (company, permitEmployees, active) => (sgkNeedsReviewFor(company, permitEmployees, active)
  ? { ...company, documents: { ...company.documents, sgk_hizmet_listesi: 'beklemede' } } : company);

const WizardStep2 = ({
  formData,
  setFormData,
  selectedCompany: rawCompany,
  selectedWorkArea,
  openCompanyDocPopover,
  setOpenCompanyDocPopover,
  handleCompanyDocUpdate,
  getSimpleDocStatusBadge,
  onCompanyFileUploaded,
  onCompanyFileDeleted,
  onCompanyDocDecided,
  getTemplatesForDoc = () => [],
  permitId,
  permitEmployees = [],
  onSendDeficiency,
  onEmployeesRefresh,
  secondary = false,
  onCompanyFieldSaved
}) => {
  const { isFirm, isReviewer } = useAuth();
  // Senaryo 2: SGK listesinde kontrol edilmemiş personel varsa onaylı liste bu talepte yeniden onay bekler
  const sgkDocs = rawCompany?.documents || {};
  const sgkReviewed = sgkDocs.sgk_hizmet_listesi_reviewed_ids || [];
  const sgkListed = sgkDocs.sgk_hizmet_listesi_listed_ids || [];
  const sgkNeedsReview = sgkNeedsReviewFor(rawCompany, permitEmployees, isReviewer && !!permitId);
  const selectedCompany = withSgkReview(rawCompany, permitEmployees, isReviewer && !!permitId);
  const firmRef = secondary ? sgkDocs : formData;
  const [personNames, setPersonNames] = useState({});
  useEffect(() => {
    if (!isReviewer) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/isg-person-names`).then(res => setPersonNames(res.data)).catch(() => {});
  }, [isReviewer]);

  // Girilen ad anında talebe kaydedilir (inceleyen talebi bitirmeden çıksa da kaybolmaz)
  const savePerson = async (field, name) => {
    if (secondary) {
      // Ekipman firması: adlar/sicil sadece o firmanın kaydına yazılır
      try {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permitId}/isg-persons`, { [field]: name }, { params: { target_company_id: rawCompany.id } });
        onCompanyFieldSaved?.(field, name);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Kaydedilemedi');
      }
      return;
    }
    setFormData(prev => ({ ...prev, [field]: name }));
    setPersonNames(prev => ({ ...prev, [field]: [...new Set([...(prev[field] || []), name])] }));
    if (!permitId) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permitId}/isg-persons`, { [field]: name });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ad kaydedilemedi');
    }
  };

  const [sgkChecked, setSgkChecked] = useState([]);
  const sgkAllReviewed = permitEmployees.every(e => sgkReviewed.includes(e.id));
  const saveSgkList = async (listDate) => {
    try {
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permitId}/sgk-check`, { sgk_listed_employee_ids: sgkChecked, sgk_list_date: formData.sgk_list_date || listDate });
      setFormData(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SGK personel teyidi kaydedilemedi');
    }
  };

  // Firma bakışı: dosya yoksa "Belge Yüklenmedi", yüklenip incelenmediyse "Onay Bekliyor"
  const statusBadge = (status, hasFile) => {
    if (status === 'beklemede') {
      return hasFile
        ? <Badge className="bg-amber-100 text-amber-700">Onay Bekliyor</Badge>
        : <Badge className="bg-slate-100 text-slate-600">Belge Yüklenmedi</Badge>;
    }
    return getSimpleDocStatusBadge(status);
  };

  const [removedKeys, setRemovedKeys] = useState([]);
  // İSG: varsayılan olarak sadece onay bekleyen (yüklenmiş, incelenmemiş) belgeler
  // İSG: varsayılan sadece onaylı belgeler; buton ile tüm belgeler
  const [showAllDocs, setShowAllDocs] = useState(false);
  // İSG: onaylı belgeler gizli gelir; onay bekleyen / eksik-hatalı / yüklenmemiş belgeler her zaman açık
  const approvedCount = COMPANY_DOCS.filter(d => selectedCompany?.documents?.[d.key] === 'onay').length;
  const tableDocs = isReviewer && !showAllDocs ? COMPANY_DOCS.filter(d => selectedCompany?.documents?.[d.key] !== 'onay') : COMPANY_DOCS;
  const [approvedDelete, setApprovedDelete] = useState(null);
  const deleteFile = async (doc, approved = false, confirmed = false) => {
    if (approved && !confirmed) { setApprovedDelete(doc); return; }
    if (!approved && !(await confirmDialog({ title: 'Belge silinsin mi?', description: <>{doc.label} belgesi silinecek. Bu işlem geri alınamaz.<DependencyRuleNote docKey={doc.key} /></>, confirmLabel: 'Evet, Sil' }))) return;
    try {
      const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${selectedCompany.id}/document-files/${doc.key}`, { params: approved ? { approved: true } : {} });
      onCompanyFileDeleted(doc.key, res.data);
      if (approved) setRemovedKeys(prev => [...prev, doc.key]);
      toast.success('Belge silindi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge silinemedi');
    }
  };
  const [showDocRequirements, setShowDocRequirements] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  useEffect(() => {
    if (viewerDoc?.docKey !== 'sgk_hizmet_listesi') return;
    const permitList = formData.sgk_listed_employee_ids || [];
    setSgkChecked(permitEmployees.filter(e => sgkListed.includes(e.id) || permitList.includes(e.id)).map(e => e.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerDoc?.docKey]);

  const requiredDocs = selectedWorkArea?.required_company_docs || [];
  const optionalDocs = COMPANY_DOCS.map(d => d.key).filter(k => !requiredDocs.includes(k));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Firma Belgeleri Kontrolü</h3>
        {!formData.equipment_company_id && <Badge variant="outline" data-testid="company-docs-firm-badge">{selectedCompany?.name}</Badge>}
      </div>
      
      
      {/* İSG Katip Uzman / İşyeri Hekimi: ilgili belge "Görüntüle ve Onayla" ekranında girilir, burada sadece gösterilir */}
      {isReviewer && <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 p-3 bg-slate-50 border rounded-lg">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">SGK Sicil No (son 4 hane)</Label>
          <div className="h-10 px-3 flex items-center rounded-md border bg-white font-mono text-sm tracking-widest" data-testid="isg-sgk-sicil">
            {firmRef.sgk_sicil_last4 ? <span className="font-bold text-slate-800">{firmRef.sgk_sicil_last4}</span> : <span className="font-sans tracking-normal text-slate-400">Belge onaylanırken girilecek</span>}
          </div>
          <p className="text-xs text-slate-500">"SGK Hizmet Listesi" belgesinde Görüntüle ve Onayla ekranından girilir</p>
        </div>
        {Object.entries(PERSON_DOCS).map(([docKey, p]) => (
          <div key={docKey} className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-2"><Users className={`h-4 w-4 ${p.color}`} />{p.label}</Label>
            <div className="h-10 px-3 flex items-center rounded-md border bg-white text-sm" data-testid={`isg-person-${docKey}`}>
              {firmRef[p.field] ? <span className="font-medium text-slate-800">{firmRef[p.field]}</span> : <span className="text-slate-400">Belge onaylanırken girilecek</span>}
            </div>
            <p className="text-xs text-slate-500">"{COMPANY_DOCS.find(d => d.key === docKey)?.label}" belgesinde Görüntüle ve Onayla ekranından girilir</p>
          </div>
        ))}
      </div>}
      
      {isReviewer && onSendDeficiency && selectedCompany?.documents?.sgk_hizmet_listesi === 'eksik' && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border-2 border-rose-400 bg-rose-50 px-3 py-2" data-testid="sgk-eksik-banner">
          <p className="text-sm font-semibold text-rose-700">SGK Hizmet Listesi eksik/hatalı. Bu belge olmazsa olmazdır; personel belgelerini kontrol etmeden talebi eksik/hatalı olarak firmaya gönderebilirsiniz.</p>
          <Button type="button" size="sm" onClick={onSendDeficiency} className="bg-rose-600 hover:bg-rose-700" data-testid="sgk-eksik-send-btn">Talebi Eksik/Hatalı Gönder</Button>
        </div>
      )}
      {isReviewer && (
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="wizard-company-docs-filter">
          <p className="text-xs text-amber-800">{showAllDocs ? 'Tüm belgeler gösteriliyor (onaylılar dahil).' : `Onay bekleyen, eksik/hatalı ve yüklenmemiş belgeler gösteriliyor. ${approvedCount} onaylı belge gizli.`}</p>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => setShowAllDocs(v => !v)} data-testid="wizard-company-toggle-all-docs">
            {showAllDocs ? 'Onaylıları gizle' : `Tüm belgeleri göster (${approvedCount} onaylı)`}
          </Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="font-semibold">Belge</TableHead>
            <TableHead className="font-semibold">Dosya</TableHead>
            <TableHead className="font-semibold w-36 text-center">Durum</TableHead>
            <TableHead className="font-semibold w-32 text-center">Geçerlilik Tarihi</TableHead>
            <TableHead className="font-semibold w-[40%] text-center">Eksiklik Detayı</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!tableDocs.length && (
            <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500" data-testid="wizard-company-no-pending">Onaylı firma belgesi yok.</TableCell></TableRow>
          )}
          {tableDocs.map(doc => {
            const status = selectedCompany?.documents?.[doc.key] || 'beklemede';
            const date = selectedCompany?.documents?.[`${doc.key}_date`] || '';
            const note = selectedCompany?.documents?.[`${doc.key}_note`] || '';
            const hasFile = !!selectedCompany?.document_files?.[doc.key];
            const pending = hasFile && status === 'beklemede';
            
            return (
              <TableRow key={doc.key} data-review-pending={isReviewer && pending ? 'true' : undefined} data-testid={`company-doc-row-${doc.key}`}>
                <TableCell className="font-medium">{doc.label}{isFirm && <PageLimitTag collection="companies" docKey={doc.key} className="ml-1.5" />}</TableCell>
                <TableCell>
                  {isFirm && hasFile && status === 'eksik' ? (
                    <FirmDeficiencyActions align="items-start"
                      entityType="companies" entityId={selectedCompany?.id} docKey={doc.key}
                      onUploaded={(info) => onCompanyFileUploaded(doc.key, info)}
                      onView={() => setViewerDoc({ docKey: doc.key, label: doc.label })}
                      testPrefix={`wizard-company-doc-${doc.key}`}
                    />
                  ) : (
                  <div className="flex flex-nowrap items-center gap-1.5 min-w-max">
                    {hasFile && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 whitespace-nowrap shrink-0" data-testid={`wizard-company-doc-uploaded-${doc.key}`}>
                        <CheckCircle className="h-3.5 w-3.5" />Belge yüklendi
                      </span>
                    )}
                    {isReviewer && <PageCountBadge info={selectedCompany?.document_files?.[doc.key]} testId={`wizard-company-doc-pages-${doc.key}`} />}
                    {hasFile && (
                      <button
                        type="button" title={isReviewer ? (status === 'onay' ? 'Görüntüle veya Eksik/Hatalı Bildir' : 'Görüntüle ve Onayla') : 'Görüntüle'}
                        onClick={() => setViewerDoc({ docKey: doc.key, label: doc.label })}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border whitespace-nowrap shrink-0 transition-colors ${isReviewer ? (status === 'onay' ? 'border-rose-600 bg-rose-600 text-white font-medium hover:bg-rose-700' : 'border-blue-900 bg-blue-900 text-white font-medium hover:bg-blue-800') : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                        data-testid={`wizard-company-doc-view-btn-${doc.key}`}
                      >
                        <Eye className="h-3.5 w-3.5" />{isReviewer ? (status === 'onay' ? 'Görüntüle veya Eksik/Hatalı Bildir' : 'Görüntüle ve Onayla') : 'Görüntüle'}
                      </button>
                    )}
                    {isFirm && hasFile && (
                      <button
                        type="button" title="Belgeyi sil" onClick={() => deleteFile(doc, status === 'onay')}
                        className="h-7 w-7 inline-flex items-center justify-center rounded border border-rose-200 text-rose-600 hover:bg-rose-50"
                        data-testid={`wizard-company-doc-delete-btn-${doc.key}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {isFirm && !pending && !(hasFile && status === 'onay') && (
                      <div className={hasFile ? 'w-28' : 'w-36'}>
                        <DocumentUploadButton
                          entityType="companies" entityId={selectedCompany?.id} docKey={doc.key} hasFile={hasFile}
                          label={hasFile ? 'Yeni sürüm' : removedKeys.includes(doc.key) ? 'Yeni sürüm yükle' : 'Belge Yükle'}
                          onUploaded={(info) => onCompanyFileUploaded(doc.key, info)}
                        />
                      </div>
                    )}
                  </div>
                  )}
                </TableCell>
                <TableCell className="text-center whitespace-nowrap">
                  <span className="inline-flex [&>*]:whitespace-nowrap" data-testid={`wizard-company-doc-status-${doc.key}`}>{statusBadge(status, hasFile)}</span>
                </TableCell>
                <TableCell className="text-center whitespace-nowrap">
                  <span className="text-xs text-slate-600">{doc.hasDate === false ? '' : ((selectedCompany?.documents?.[`${doc.key}_validity`] || date || '').slice(0, 10).split('-').reverse().join('.')) || '-'}</span>
                </TableCell>
                <TableCell>
                  <div className="text-center">{status === 'eksik' && note ? (
                    <div className="text-xs text-rose-600 whitespace-pre-wrap break-words" title={note}>
                      {note}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}</div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <AlertDialog open={!!approvedDelete} onOpenChange={(o) => !o && setApprovedDelete(null)}>
        <AlertDialogContent data-testid="approved-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="h-5 w-5" />Onaylı belgeyi siliyorsunuz</AlertDialogTitle>
            <AlertDialogDescription data-testid="approved-delete-message">
              <b>{approvedDelete?.label}</b> onaylı bir belgedir. Eğer silerseniz tekrar onay sürecine gireceğinden yeni belge yüklemeniz gerekmektedir.
              <DependencyRuleNote docKey={approvedDelete?.key} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approved-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => { const d = approvedDelete; setApprovedDelete(null); deleteFile(d, true, true); }} data-testid="approved-delete-confirm">Evet, Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DocumentViewerDialog
        open={!!viewerDoc}
        onOpenChange={(open) => !open && setViewerDoc(null)}
        entity={selectedCompany}
        entityType="companies"
        subtitle={selectedCompany?.name}
        docKey={viewerDoc?.docKey}
        docLabel={viewerDoc?.label}
        mode="latest"
        compact={isFirm}
        onReplaced={isReviewer && viewerDoc ? (info) => onCompanyFileUploaded(viewerDoc.docKey, info) : undefined}
        extraInfo={viewerDoc && isFirm && (
          <FirmDeficiencyExtra
            status={selectedCompany?.documents?.[viewerDoc.docKey]} docLabel={viewerDoc.label} note={selectedCompany?.documents?.[`${viewerDoc.docKey}_note`]}
            entityType="companies" entityId={selectedCompany?.id} docKey={viewerDoc.docKey}
            onUploaded={(info) => { onCompanyFileUploaded(viewerDoc.docKey, info); setViewerDoc(null); }}
          />
        )}
        decisionPanel={viewerDoc && isReviewer && (
          <DocDecisionPanel
            key={viewerDoc.docKey}
            url={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${selectedCompany.id}/documents/${viewerDoc.docKey}/decision`}
            docLabel={viewerDoc.label}
            approved={selectedCompany?.documents?.[viewerDoc.docKey] === 'onay'}
            templates={getTemplatesForDoc(viewerDoc.docKey).map(t => t.template_text)}
            validity={selectedCompany?.documents?.[`${viewerDoc.docKey}_validity`]}
            onDecided={(res) => { onCompanyDocDecided(viewerDoc.docKey, res); setViewerDoc(null); }}
            hasDate={COMPANY_DOCS.find(d => d.key === viewerDoc.docKey)?.hasDate !== false}
            personLabel={PERSON_DOCS[viewerDoc.docKey]?.label}
            onPersonSaved={(name) => savePerson(PERSON_DOCS[viewerDoc.docKey].field, name)}
            suggestions={personNames[PERSON_DOCS[viewerDoc.docKey]?.field] || []}
            sicilMode={viewerDoc.docKey === 'sgk_hizmet_listesi' ? 'enter' : PERSON_DOCS[viewerDoc.docKey] ? 'match' : undefined}
            refSicil={(secondary ? sgkDocs.sgk_sicil_last4 : formData.sgk_sicil_last4) || ''}
            initialSicil={(secondary ? sgkDocs.sgk_sicil_last4 : formData.sgk_sicil_last4) || ''}
            docSicil={(secondary ? '' : formData[`${viewerDoc.docKey}_sicil_last4`]) || selectedCompany?.documents?.[`${viewerDoc.docKey}_sicil_last4`] || ''}
            extra={viewerDoc.docKey === 'sgk_hizmet_listesi' && permitId && (!sgkAllReviewed || sgkNeedsReview ? (
              <SgkEmployeeChecklist employees={permitEmployees} checked={sgkChecked} onChange={setSgkChecked} />
            ) : (
              <p className="rounded border border-sky-200 bg-sky-50 p-2 text-xs text-sky-800" data-testid="sgk-checklist-kept">Talepteki tüm personel bu SGK Hizmet Listesi için daha önce kontrol edildi; mevcut SGK işaretleri korunur.</p>
            ))}
            extraBody={viewerDoc.docKey === 'sgk_hizmet_listesi' && permitId ? { reviewed_employee_ids: permitEmployees.map(e => e.id), listed_employee_ids: sgkChecked } : undefined}
            onApproved={viewerDoc.docKey === 'sgk_hizmet_listesi' ? async (date) => {
              if (permitId && !secondary) await saveSgkList(date);
              await onEmployeesRefresh?.();
            } : undefined}
            onSicilSaved={(v) => savePerson(viewerDoc.docKey === 'sgk_hizmet_listesi' ? 'sgk_sicil_last4' : `${viewerDoc.docKey}_sicil_last4`, v)}
          />
        )}
      />
    </div>
  );
};

export default WizardStep2;
